import { Vector3 } from "three";

export class Node<T>{
    constructor(value: T, id: number) {
        this.value = value;
        this._id = id;
    }

    static Create<T>(value: T) {
        return new Node<T>(value, -1);
    }

    private _id: number;
    public get id(): number {
        return this._id;
    }
    value: T;
    prev: Node<T> | null = null;
    next: Node<T> | null = null;

    equals(other: Node<T> | T) {
        if (other instanceof Vector3 && this.value instanceof Vector3) {
            let value = this.value.clone();
            return ((Math.abs(value.x - other.x) < Number.EPSILON) && (Math.abs(value.y - other.y) < Number.EPSILON) && (Math.abs(value.z - other.z) < Number.EPSILON));
        }
        else if ((other as any).value instanceof Vector3 && this.value instanceof Vector3) {
            let lhs = this.value.clone();
            let rhs = (other as any).value.clone()
            return ((Math.abs(lhs.x - rhs.x) < Number.EPSILON) && (Math.abs(lhs.y - rhs.y) < Number.EPSILON) && (Math.abs(lhs.z - rhs.z) < Number.EPSILON));
        }
        else if (other as Node<T>)
            return this.value === (other as Node<T>).value;
        else
            return this.value === other;
    };
}

export class VertexNode extends Node<Vector3>{
    override equals(other: VertexNode | Vector3) {
        if (other instanceof VertexNode)
            return ((Math.abs(this.value.x - other.value.x) < Number.EPSILON) && (Math.abs(this.value.y - other.value.y) < Number.EPSILON) && (Math.abs(this.value.z - other.value.z) < Number.EPSILON));
        return ((Math.abs(this.value.x - other.x) < Number.EPSILON) && (Math.abs(this.value.y - other.y) < Number.EPSILON) && (Math.abs(this.value.z - other.z) < Number.EPSILON));
    }
};

export class Edge<T>{
    constructor(start: Node<T>, end: Node<T>) {
        this.start = start;
        this.end = end;
    }
    static Create<T>(start: T, end: T) {
        return new Edge<T>(Node.Create(start), Node.Create(end));
    }
    start: Node<T>;
    end: Node<T>;
    get twin(): Edge<T> {
        return new Edge<T>(this.end, this.start);
    }

    equals(other: Edge<T>) {
        return this.start.equals(other.start) && this.end.equals(other.end);
    };

    isConnected(other: Edge<T>) {
        return this.start.equals(other.start) || this.end.equals(other.end)
            || this.start.equals(other.end) || this.end.equals(other.start)
    }

    overlaps(other: Edge<T>) {
        return this.start.equals(other.start) || this.end.equals(other.end);
    };

    isTwin(other: Edge<T>) {
        return this.equals(other.twin);
    }
}
export class VertexEdge extends Edge<Vector3>{
    get distance(): number {
        return this.start.value.distanceTo(this.end.value);
    }

    get direction(): Vector3 {
        return this.end.value.clone().sub(this.start.value).normalize();
    }
}

export class List<T> {

    constructor(head: Node<T>, tail: Node<T>) {
        this._head = head;
        this._tail = tail;
    }


    private _head: Node<T>;
    public get head(): Node<T> {
        return this._head;
    }

    private _tail: Node<T>;
    public get tail(): Node<T> {
        return this._tail;
    }

    public append(node: Node<T>) {
        if (this._tail) {
            this.tail!.next = node;
            node.prev = this.tail;
            this._tail = node;
        }
        else {
            this._head = node;
            this._tail = node;
        }
    }

    public appendChain(node: Node<T>) {
        this.append(node);
        while (node.next !== null) {
            node = node.next;
        }
        this._tail = node;
    }

    public unshift(node: Node<T>) {
        if (this._head) {
            this._head.prev = node;
            node.next = this._head;
            this._head = node;
        }
        else {
            this._head = node;
            this._tail = node;
        }
    }

    public contains(node: Node<T>) {
        let curr: Node<T> | null = this.head;
        while (curr) {
            if (curr.id === node.id)
                return true;
            curr = curr.next;
        }
        return false;
    }


    /**
     * Will insert the given newNode before the given node.
     * @param node The node before which the newNode will be inserted
     * @param newNode The new node to insert
     */
    public insertBefore(node: Node<T>, newNode: Node<T>) {
        let prev = node.prev;
        // If node is not head
        if (prev) {
            prev.next = newNode;
            newNode.prev = prev;
        }
        else { // assign newnode as head
            this._head = newNode;
        }
        node.prev = newNode;
        newNode.next = node;
    }

    /**
     * Will insert the given newNode after the given node.
     * @param node The node after which the newNode will be inserted
     * @param newNode The new node to insert
     */
    public insertAfter(node: Node<T>, newNode: Node<T>) {
        let next = node.next;
        if (next) { // if not at tail
            next.prev = newNode;
            newNode.next = next;
        }
        else { // assign newnode as tail
            this._tail = newNode;
        }
        node.next = newNode;
        newNode.prev = node;
    }

    /**
     * Clears all nodes and removes their prev, enxt and value refs.
     */
    public clear() {
        let node: Node<T> | null = this._head;
        while (node) {
            let next = node.next;
            next!.prev = null;
            node.next = null;
            node = next;
        }
    }

    public get length(): number {
        let length = 0;
        let node: Node<T> | null = this.head;
        while (node) {
            ++length;
            node = node.next;
        }
        return length;
    }

    public reverse() {
        let node: Node<T> | null = this._head;
        this._tail = node;
        while (node) {
            let next = node.next;
            node.next = node.prev;
            node.next = next;
            if (!next) {
                this._head = node;
            }
            node = next;
        }
    }

    public removeNode(node: Node<T>) {
        let prev = node.prev;
        prev!.next = node.next;
        node.next!.prev = prev;
    }

    public find(predicate: (node: Node<T>) => boolean) {
        let node: Node<T> | null = this._head;
        while (node) {
            if (predicate(node))
                break;
            node = node.next;
        }
        return node;
    }

    public get values(): T[] {
        let values: T[] = [];
        let node: Node<T> | null = this.head;
        while (node) {
            values.push(node!.value);
            node = node.next;
        }
        return values;
    }

    public get edges(): Edge<T>[] {
        let edges: Edge<T>[] = [];
        let node = this.head;
        let run = 0;
        while (node?.next && run < 100) {
            ++run;
            edges.push(new Edge<T>(node, node.next));
            node = node.next;
        }
        edges.push(new Edge<T>(this.tail, this.head))
        return edges;
    }

    public findEdge(a: Node<T>, b: Node<T>): Edge<T> | null {
        let hit: Edge<T> | null = null;
        let node: Node<T> | null = this.head;
        let run = 0;
        while (node && !hit && run < 100) {
            if (node.equals(a)) {
                if (a?.prev?.equals(b))
                    return new Edge<T>(b, a);
                if (a?.next?.equals(b))
                    return new Edge<T>(a, b);
                return null;
            }
            ++run;
            node = node.next;
        }
        return null;
    }

    public get path(): string {
        let path = '';
        let node: Node<T> | null = this.head;
        while (node) {
            path += `${node.id}`;
            if (node.next)
                path += ' -> ';
            node = node.next;
        }
        return path;
    }
}

export class VertexList extends List<Vector3>{ };
